import React, { useEffect } from 'react'
import { AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Badge,
} from '../ui'
import { useDeleteEmployee, type Employee } from '../../hooks/useEmployees'
import { LoadingSpinner } from '../ui/loading-states'

interface DeleteEmployeeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employee: Employee | null
}

export function DeleteEmployeeDialog({
  open,
  onOpenChange,
  employee
}: DeleteEmployeeDialogProps) {
  const deleteEmployee = useDeleteEmployee()
  const isLoading = deleteEmployee.isPending

  // Сброс состояния ошибки при открытии/закрытии диалога
  useEffect(() => {
    if (!open && deleteEmployee.error) {
      deleteEmployee.reset()
    }
  }, [open, deleteEmployee])

  const handleDelete = async () => {
    if (!employee) return

    try {
      await deleteEmployee.mutateAsync(employee.id)
      // Закрываем диалог сразу после успешного удаления
      onOpenChange(false)
    } catch (error: any) {
      // Ошибка будет показана в UI через deleteEmployee.error
      // Диалог остается открытым чтобы показать ошибку
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false)
    }
  }

  if (!employee) return null

  const getFullName = (employee: Employee) => {
    const parts = [employee.last_name, employee.first_name, employee.middle_name].filter(Boolean)
    return parts.join(' ')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <DialogTitle>
                <span className="text-red-900">Удалить сотрудника?</span>
              </DialogTitle>
              <DialogDescription>
                Это действие нельзя будет отменить.
              </DialogDescription>
            </div>
          </div>
          {!isLoading && <DialogClose onClose={handleClose} />}
        </DialogHeader>

        <div className="py-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium text-gray-700">ФИО:</span>
                <p className="text-sm text-gray-900">{getFullName(employee)}</p>
              </div>

              <div>
                <span className="text-sm font-medium text-gray-700">Табельный номер:</span>
                <p className="text-sm text-gray-900">№{employee.employee_number}</p>
              </div>

              {employee.iin && (
                <div>
                  <span className="text-sm font-medium text-gray-700">ИИН:</span>
                  <p className="text-sm text-gray-900">{employee.iin}</p>
                </div>
              )}

              {employee.department && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Подразделение:</span>
                  <p className="text-sm text-gray-900">{employee.department.name}</p>
                </div>
              )}

              {employee.position && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Должность:</span>
                  <p className="text-sm text-gray-900">{employee.position.name}</p>
                </div>
              )}

              {employee.email && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Email:</span>
                  <p className="text-sm text-gray-900">{employee.email}</p>
                </div>
              )}

              {employee.code_1c && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Код 1С:</span>
                  <p className="text-sm text-gray-900">{employee.code_1c}</p>
                </div>
              )}

              <div>
                <span className="text-sm font-medium text-gray-700">Статус:</span>
                <div className="mt-1">
                  {employee.is_active ? (
                    <Badge variant="default" className="bg-green-100 text-green-800">Активный</Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-red-100 text-red-800">Неактивный</Badge>
                  )}
                </div>
              </div>

              <div>
                <span className="text-sm font-medium text-gray-700">Источник:</span>
                <div className="mt-1">
                  {employee.code_1c ? (
                    <Badge variant="secondary">1С</Badge>
                  ) : (
                    <Badge variant="outline">TCO</Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Warning about consequences */}
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-start">
              <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium">Что произойдет:</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Если у сотрудника есть записи учета времени или посещаемости, он будет деактивирован (статус "Неактивный")</li>
                  <li>Если нет исторических данных, сотрудник будет полностью удален из системы</li>
                  <li>В любом случае сотрудник исчезнет из активных списков</li>
                  <li>Это действие нельзя отменить</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Show API errors */}
          {deleteEmployee.error && (
            <div className="mt-4 flex items-center p-3 text-sm text-red-700 bg-red-100 rounded-md">
              <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>
                {(() => {
                  const error = deleteEmployee.error as any
                  // API клиент возвращает ошибку с полями message и status напрямую
                  const message = error?.message
                  const status = error?.status

                  if (message === 'Employee not found' || status === 404) {
                    return 'Сотрудник не найден. Возможно, он уже был удален. Попробуйте обновить страницу.'
                  } else if (status === 403) {
                    return 'У вас нет прав для удаления этого сотрудника.'
                  } else if (status === 409) {
                    return 'Невозможно удалить сотрудника с активными записями учета времени. Сначала архивируйте или удалите связанные записи.'
                  } else if (status === 500) {
                    return 'Произошла ошибка на сервере. Попробуйте позже.'
                  } else {
                    return message || 'Произошла неизвестная ошибка при удалении'
                  }
                })()}
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Отменить
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
            className="min-w-[100px]"
          >
            {isLoading ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                Удаление...
              </>
            ) : (
              'Удалить сотрудника'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}